import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { slideDown } from '../animations/variants'
import { Mail, Globe, MessageCircle, Send } from 'lucide-react'

const MotionLink = motion(Link)

export default function Header() {
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [menuOpen])

  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname])

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Projects', path: '/projects' },
    { name: 'Games', path: '/games' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'About', path: '/about' },
  ]

  return (
    <>
      <header
        style={{
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          background: scrolled ? 'rgba(2,4,7,0.75)' : 'rgba(2,4,7,0.25)',
          borderBottom: `1px solid ${scrolled ? 'rgba(0,217,255,0.1)' : 'rgba(255,255,255,0.04)'}`,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          width: '100%',
          zIndex: 1000,
          transition: 'background 0.3s ease, border-color 0.3s ease',
        }}
      >
        <motion.nav
          initial="hidden"
          animate="visible"
          variants={slideDown}
          className="max-w-6xl mx-auto px-4 py-3 flex justify-between items-center"
          style={{ paddingLeft: isMobile ? '1rem' : '1rem', paddingRight: isMobile ? '1rem' : '1rem' }}
        >
          <MotionLink
            to="/"
            whileHover={{ scale: 1.02, textShadow: '0 0 40px #00d9ff' }}
            transition={{ duration: 0.2 }}
            style={{
              color: '#00d9ff',
              fontWeight: 700,
              letterSpacing: '0.05em',
              fontSize: isMobile ? '1.25rem' : '1.5rem',
              textDecoration: 'none',
              textShadow: '0 0 20px #00d9ff',
            }}
          >
            Rohan Dalvi
          </MotionLink>

          {!isMobile && (
            <div className="flex gap-8">
              {navLinks.map((link, index) => {
                const isActive = location.pathname === link.path
                return (
                    <motion.div
                      key={link.path}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      whileHover={{ y: -1 }}
                      transition={{ delay: 0.1 + index * 0.05, duration: 0.2 }}
                    >
                      <Link
                        to={link.path}
                        style={{
                          color: isActive ? '#00d9ff' : (scrolled ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.55)'),
                          borderBottom: isActive ? '2px solid #00d9ff' : '2px solid transparent',
                          paddingBottom: '2px',
                          transition: 'color 0.3s ease',
                          textDecoration: 'none',
                        }}
                        onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.color = '#ffffff' }}
                        onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.color = scrolled ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.55)' }}
                      >
                        {link.name}
                      </Link>
                    </motion.div>
                )
              })}
            </div>
          )}

          {isMobile && (
            <button
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              style={{ color: scrolled ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.55)', background: 'none', border: 'none', padding: '8px', transition: 'color 0.3s ease' }}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          )}
        </motion.nav>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ clipPath: 'circle(0% at calc(100% - 2rem) 30px)' }}
            animate={{ clipPath: 'circle(150% at calc(100% - 2rem) 30px)' }}
            exit={{ clipPath: 'circle(0% at calc(100% - 2rem) 30px)' }}
            transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
            style={{
              position: 'fixed', inset: 0,
              zIndex: 999,
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              background: 'rgba(2,4,7,0.95)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'center',
              padding: '0 2rem',
              gap: '0.25rem',
              overflowY: 'auto'
            }}
          >
            <div style={{
              position: 'absolute', inset: 0,
              backgroundImage: 'linear-gradient(rgba(0,217,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,217,255,0.04) 1px, transparent 1px)',
              backgroundSize: '40px 40px',
              pointerEvents: 'none'
            }} />

            <div style={{
              position: 'absolute',
              top: '1.25rem',
              left: '2rem',
              fontSize: '0.75rem',
              color: 'rgba(0,217,255,0.6)',
              fontFamily: 'monospace',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              zIndex: 2
            }}>MENU</div>

            {[
              { label: 'Home', href: '/', num: '01' },
              { label: 'Projects', href: '/projects', num: '02' },
              { label: 'Games', href: '/games', num: '03' },
              { label: 'Gallery', href: '/gallery', num: '04' },
              { label: 'About', href: '/about', num: '05' },
            ].map((item, i) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, x: -40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ delay: 0.15 + i * 0.07, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                style={{ width: '100%' }}
              >
                <Link
                  to={item.href}
                  onClick={() => setMenuOpen(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '0.75rem',
                    textDecoration: 'none',
                    padding: '0.75rem 0',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  <span style={{
                    fontSize: '0.6rem',
                    color: location.pathname === item.href ? '#00d9ff' : 'rgba(0,217,255,0.5)',
                    fontFamily: 'monospace',
                    letterSpacing: '0.1em',
                    transition: 'color 0.2s'
                  }}>{item.num}</span>
                  <span style={{
                    fontSize: 'clamp(1.5rem, 8vw, 2.5rem)',
                    fontWeight: 900,
                    color: location.pathname === item.href ? '#00d9ff' : 'white',
                    letterSpacing: '-0.02em',
                    textTransform: 'uppercase',
                    lineHeight: 1.1,
                    transition: 'color 0.2s'
                  }}>{item.label}</span>
                </Link>
              </motion.div>
            ))}

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              style={{
                display: 'flex', gap: '1.25rem',
                marginTop: '1.5rem', position: 'relative', zIndex: 1,
                paddingTop: '1rem',
                borderTop: '1px solid rgba(255,255,255,0.06)'
              }}
            >
              {[
                { icon: Mail, href: 'mailto:dalvirohan369@gmail.com', label: 'Email' },
                { icon: Globe, href: 'https://linkedin.com/in/rohan-dalvi-552006377', label: 'LinkedIn' },
                { icon: MessageCircle, href: 'https://instagram.com/rohan_sys_', label: 'Instagram' },
                { icon: Send, href: 'https://twitter.com/dalvirohan369', label: 'Twitter' },
              ].map(s => (
                <a key={s.href} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}
                  style={{ color: 'rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', transition: 'color 0.2s', textDecoration: 'none', padding: '8px' }}
                  onTouchStart={(e) => { e.currentTarget.style.color = '#00d9ff' }}
                  onTouchEnd={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)' }}
                >
                  <s.icon size={20} />
                </a>
              ))}
            </motion.div>

          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
